import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  MoreVertical, 
  Calendar, 
  User, 
  Tag, 
  MessageSquare, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  Star, 
  Archive, 
  Share2, 
  Copy, 
  ExternalLink,
  ChevronRight,
  ChevronDown,
  CheckSquare,
  Play,
  Pause,
  Zap,
  Target,
  TrendingUp,
  BarChart3,
  Activity,
  Award,
  Lightbulb,
  Settings,
  Eye,
  Link,
  FileText,
  Users,
  GitBranch,
  GitCommit,
  GitPullRequest,
  Check,
  X,
  Save,
  RotateCcw,
  Flag,
  Bookmark,
  Heart,
  ThumbsUp,
  MessageCircle,
  Paperclip,
  Image,
  Video,
  File,
  Download,
  Upload
} from 'lucide-react';
import { Task } from '../types';
import { useTheme } from '../contexts/ThemeContext';

interface TaskDetailProps {
  task: Task;
  subTasks: Task[];
  onEdit: (task: Task) => void;
  onDelete: (taskId: string) => void;
  onSubTaskClick: (task: Task) => void;
  onAddSubTask: (parentTaskId: string) => void;
  onStatusChange: (taskId: string, status: string) => void;
  onPriorityChange: (taskId: string, priority: string) => void;
  onAssignUser: (taskId: string, userId: string) => void;
  isEditing?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
}

const TaskDetail: React.FC<TaskDetailProps> = ({
  task,
  subTasks,
  onEdit,
  onDelete,
  onSubTaskClick,
  onAddSubTask,
  onStatusChange,
  onPriorityChange,
  onAssignUser,
  isEditing = false,
  onSave,
  onCancel
}) => {
  const { getThemeColors } = useTheme();
  const colors = getThemeColors();
  
  const [expandedSections, setExpandedSections] = useState({
    description: true,
    subTasks: true,
    comments: false,
    activity: false,
    attachments: false
  });
  
  const [taskData, setTaskData] = useState({
    title: task.title,
    description: task.description || '',
    priority: task.priority,
    due_date: task.due_date || '',
    status: task.status,
    estimated_hours: task.estimated_hours || 0,
    tags: task.tags || ''
  });

  const [newComment, setNewComment] = useState('');

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'from-green-500 to-emerald-600';
      case 'in-progress': return 'from-blue-500 to-indigo-600';
      case 'overdue': return 'from-red-500 to-pink-600';
      case 'todo': return 'from-gray-500 to-slate-600';
      default: return 'from-gray-500 to-slate-600';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'from-red-500 to-pink-600';
      case 'medium': return 'from-yellow-500 to-orange-600';
      case 'low': return 'from-green-500 to-emerald-600';
      default: return 'from-gray-500 to-slate-600';
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleStatusChange = (newStatus: string) => {
    onStatusChange(task.id, newStatus);
  };

  const handlePriorityChange = (newPriority: string) => {
    onPriorityChange(task.id, newPriority);
  };

  const addComment = () => {
    if (newComment.trim()) {
      // TODO: Implement comment functionality
      setNewComment('');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Task Header */}
      <div className="space-y-4">
        {isEditing ? (
          <div className="space-y-4">
            <input
              type="text"
              value={taskData.title}
              onChange={(e) => setTaskData(prev => ({ ...prev, title: e.target.value }))}
              className="text-2xl font-bold bg-transparent border-b border-slate-300 dark:border-slate-600 focus:border-blue-500 focus:outline-none w-full"
            />
            <textarea
              value={taskData.description}
              onChange={(e) => setTaskData(prev => ({ ...prev, description: e.target.value }))}
              className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
              placeholder="Add task description..."
              rows={3}
            />
            <div className="grid grid-cols-2 gap-4">
              <select
                value={taskData.priority}
                onChange={(e) => setTaskData(prev => ({ ...prev, priority: e.target.value as 'low' | 'medium' | 'high' }))}
                className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
              </select>
              <select
                value={taskData.status}
                onChange={(e) => setTaskData(prev => ({ ...prev, status: e.target.value as 'todo' | 'in-progress' | 'completed' | 'overdue' }))}
                className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              >
                <option value="todo">To Do</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="overdue">Overdue</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input
                type="date"
                value={taskData.due_date}
                onChange={(e) => setTaskData(prev => ({ ...prev, due_date: e.target.value }))}
                className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
              />
              <input
                type="number"
                value={taskData.estimated_hours}
                onChange={(e) => setTaskData(prev => ({ ...prev, estimated_hours: parseFloat(e.target.value) || 0 }))}
                className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
                placeholder="Est. hours"
                step="0.5"
              />
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={onSave}
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
              >
                Save Changes
              </button>
              <button
                onClick={onCancel}
                className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                  {task.title}
                </h1>
                {task.description && (
                  <p className="text-slate-600 dark:text-slate-400 mb-4">
                    {task.description}
                  </p>
                )}
                <div className="flex items-center space-x-4 text-sm">
                  <span className={`px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getStatusColor(task.status)} text-white`}>
                    {task.status}
                  </span>
                  <span className={`px-3 py-1 text-xs font-medium rounded-full bg-gradient-to-r ${getPriorityColor(task.priority)} text-white`}>
                    {task.priority} priority
                  </span>
                  {task.due_date && (
                    <span className="flex items-center text-slate-600 dark:text-slate-400">
                      <Calendar className="w-4 h-4 mr-1" />
                      Due {new Date(task.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => onEdit(task)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  title="Edit task"
                >
                  <Edit className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                </button>
                <button
                  onClick={() => onDelete(task.id)}
                  className="p-2 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  title="Delete task"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => handleStatusChange('todo')}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
            task.status === 'todo' 
              ? 'bg-slate-200 text-slate-800 dark:bg-slate-600 dark:text-white' 
              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600'
          }`}
        >
          To Do
        </button>
        <button
          onClick={() => handleStatusChange('in-progress')}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
            task.status === 'in-progress' 
              ? 'bg-blue-200 text-blue-800 dark:bg-blue-600 dark:text-white' 
              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-blue-200 dark:hover:bg-blue-600'
          }`}
        >
          In Progress
        </button>
        <button
          onClick={() => handleStatusChange('completed')}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
            task.status === 'completed' 
              ? 'bg-green-200 text-green-800 dark:bg-green-600 dark:text-white' 
              : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400 hover:bg-green-200 dark:hover:bg-green-600'
          }`}
        >
          Complete
        </button>
      </div>

      {/* Task Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <CheckSquare className="w-5 h-5 text-blue-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Sub Tasks</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{subTasks.length}</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <Clock className="w-5 h-5 text-orange-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Est. Hours</span>
          </div>
          <p className="text-2xl font-bold text-slate-900 dark:text-white">{task.estimated_hours || 0}</p>
        </div>
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <User className="w-5 h-5 text-purple-500" />
            <span className="text-sm text-slate-600 dark:text-slate-400">Assigned</span>
          </div>
          <p className="text-lg font-medium text-slate-900 dark:text-white">
            {task.assigned_user_name || 'Unassigned'}
          </p>
        </div>
      </div>

      {/* Sub Tasks Section */}
      <div className="bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600/50"
          onClick={() => toggleSection('subTasks')}
        >
          <div className="flex items-center space-x-3">
            <CheckSquare className="w-5 h-5 text-blue-500" />
            <h3 className="font-semibold text-slate-900 dark:text-white">Sub Tasks</h3>
            <span className="text-sm text-slate-500 dark:text-slate-400">
              ({subTasks.length})
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={(e) => { e.stopPropagation(); onAddSubTask(task.id); }}
              className="p-2 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
              title="Add sub task"
            >
              <Plus className="w-4 h-4 text-blue-600" />
            </button>
            {expandedSections.subTasks ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </div>
        </div>
        
        {expandedSections.subTasks && (
          <div className="border-t border-slate-200 dark:border-slate-600">
            {subTasks.length === 0 ? (
              <div className="p-6 text-center text-slate-500 dark:text-slate-400">
                <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No sub tasks yet</p>
                <button
                  onClick={() => onAddSubTask(task.id)}
                  className="mt-3 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  Add Sub Task
                </button>
              </div>
            ) : (
              <div className="divide-y divide-slate-200 dark:divide-slate-600">
                {subTasks.map((subTask) => (
                  <div
                    key={subTask.id}
                    className="p-4 hover:bg-slate-50 dark:hover:bg-slate-600/50 cursor-pointer transition-colors"
                    onClick={() => onSubTaskClick(subTask)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${
                          subTask.status === 'completed' ? 'bg-green-500' :
                          subTask.status === 'in-progress' ? 'bg-blue-500' :
                          subTask.status === 'overdue' ? 'bg-red-500' : 'bg-slate-300'
                        }`} />
                        <div>
                          <h4 className="font-medium text-slate-900 dark:text-white">
                            {subTask.title}
                          </h4>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {subTask.assigned_user_name || 'Unassigned'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          subTask.priority === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :
                          subTask.priority === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                        }`}>
                          {subTask.priority}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Comments Section */}
      <div className="bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600/50"
          onClick={() => toggleSection('comments')}
        >
          <div className="flex items-center space-x-3">
            <MessageSquare className="w-5 h-5 text-green-500" />
            <h3 className="font-semibold text-slate-900 dark:text-white">Comments</h3>
            <span className="text-sm text-slate-500 dark:text-slate-400">(0)</span>
          </div>
          {expandedSections.comments ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </div>
        
        {expandedSections.comments && (
          <div className="border-t border-slate-200 dark:border-slate-600 p-4">
            <div className="space-y-4">
              <div className="flex space-x-3">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  U
                </div>
                <div className="flex-1">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white resize-none"
                    placeholder="Add a comment..."
                    rows={3}
                  />
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center space-x-2">
                      <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors">
                        <Paperclip className="w-4 h-4 text-slate-400" />
                      </button>
                      <button className="p-2 hover:bg-slate-100 dark:hover:bg-slate-600 rounded-lg transition-colors">
                        <Image className="w-4 h-4 text-slate-400" />
                      </button>
                    </div>
                    <button
                      onClick={addComment}
                      disabled={!newComment.trim()}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                      Comment
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Activity Section */}
      <div className="bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-600/50"
          onClick={() => toggleSection('activity')}
        >
          <div className="flex items-center space-x-3">
            <Activity className="w-5 h-5 text-green-500" />
            <h3 className="font-semibold text-slate-900 dark:text-white">Recent Activity</h3>
          </div>
          {expandedSections.activity ? (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          )}
        </div>
        
        {expandedSections.activity && (
          <div className="border-t border-slate-200 dark:border-slate-600 p-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span className="text-slate-600 dark:text-slate-400">Task created</span>
                <span className="text-slate-400">2 days ago</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-slate-600 dark:text-slate-400">Status changed to "In Progress"</span>
                <span className="text-slate-400">1 day ago</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskDetail;
